import { useEffect, useState } from 'react';
import { PassKeyCardItem } from './PassKeyCardItem';
import { Button, Notification } from '@unified-checkout/design-system';
import {
  useListPasskeys,
  usePasskeyRegister,
  useVerifyPasskeyRegister,
} from 'apps/customer/src/services/inner_layout/passkey';
import { startRegistration } from '@simplewebauthn/browser';
import { userDeviceInfo, useWidth } from '@unified-checkout/kit-components';
import { BackButton, Spinner } from 'apps/customer/src/components';
import { IPasskey } from 'apps/customer/src/types';
import { Key } from 'apps/customer/src/assets/icons';
import { MOBILE_BREAKPOINT } from 'apps/customer/src/constants';
import { getAPIURLByDomain } from 'apps/customer/src/utils/apiUrl';
import { useNavigate } from 'react-router-dom';
import EmptyPassKeyList from './EmptyPassKeyList';

export const PassKeys = () => {
  const isMobile = useWidth(MOBILE_BREAKPOINT);
  const navigate = useNavigate()
  
  const {
    data: ListPasskeysResponse,
    isLoading: isLoadingPasskeys
  } = useListPasskeys();

  const { mutateAsync: passkeyRegister, isPending: isPendingPasskeyRegister } =
    usePasskeyRegister();

  const {
    mutate: verifyPasskeyRegisterMutation,
    isPending: isPendingVerifyPasskeyRegister,
    isSuccess: isSuccessVerifyPasskeyRegister,
  } = useVerifyPasskeyRegister();

  const [isSuccessAddingPasskey, setIsSuccessAddingPasskey] = useState(false);

  useEffect(() => {
    if (isSuccessVerifyPasskeyRegister) setIsSuccessAddingPasskey(true);
  }, [isSuccessVerifyPasskeyRegister]);

  const handleAddPasskey = async () => {
    let credential;

    const passKeyOptions = await passkeyRegister();

    credential = await startRegistration({
      optionsJSON: passKeyOptions.publicKey as any,
    });

    // The backend verifies the credential against its own origin rather than
    // this app's, so the client data is rewritten with the API host this build
    // talks to. It used to be hardcoded to alpha, which sent every production
    // registration an origin its backend would reject.
    const editedResponse = {
      ...credential.response,
      clientDataJSON: btoa(
        JSON.stringify({
          ...JSON.parse(atob(credential.response.clientDataJSON)),
          origin: getAPIURLByDomain(),
        })
      ),
    };
    verifyPasskeyRegisterMutation({
      credential: {
        ...credential,
        response: editedResponse,
        extra: userDeviceInfo,
      },
    });
  };

  return (
    <section className="flex flex-col grow ">
      <BackButton onClick={() => navigate(-1)} />
      <h2 className="page-title">Passkeys</h2>
      {
        !isLoadingPasskeys && !!ListPasskeysResponse?.length && (
      <h2 className="mb-3 text-[#667085] md:mb-6 english-utility-label-medium">
        Your saved passkeys on devices
      </h2>
        )
      }
      <section className="flex-grow space-y-3">
        {isLoadingPasskeys ? (
          <Spinner />
        ) : ListPasskeysResponse ? (
          ListPasskeysResponse.length > 0 ? ListPasskeysResponse?.map((passkey: IPasskey) => (
            <PassKeyCardItem passkey={passkey} />
          )) : (
            <EmptyPassKeyList 
              handleAddPasskey={handleAddPasskey}
              isPendingPasskeyRegister={isPendingPasskeyRegister}
              isPendingVerifyPasskeyRegister={isPendingVerifyPasskeyRegister}
            />
          )
        ) : null}
      </section>

      {isSuccessAddingPasskey && (
        <Notification
          show={isSuccessAddingPasskey}
          title="Passkey added successfully"
          icon={Key}
          isMobile={isMobile}
          hasCloseIcon
          onCloseHandler={() => setIsSuccessAddingPasskey(false)}
          state="primary"
          timeout={5}
        />
      )}
      {!isLoadingPasskeys &&ListPasskeysResponse?.length !== 0 &&  
      <div className="[&_button]:w-full mt-5 mb-6">
        <Button
          color="primary"
          text="Add a new passkey"
          size="md"
          type="button"
          onClickHandler={handleAddPasskey}
          disabled={isPendingPasskeyRegister || isPendingVerifyPasskeyRegister}
        />
      </div>
      }
    </section>
  );
};
